import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { lg, xs } from '@exonet/react-shared/lib/styled';

// React-shared.
import Button from 'app/client/components/Button';

// App components.
import Logo from 'app/client/components/Logo';

export const Menu = styled.nav`
  flex: 0 0 100%;
  background-color: var(--menu-bg);
  color: var(--bg);
  flex-direction: column;
  display: flex;
  flex-flow: column nowrap;
  position: fixed;
  z-index: 999;
  left: -250px;
  height: 100vh;
  /* iOS Safari counts its own chrome in 100vh, hiding the footer. */
  height: 100dvh;
  width: 250px;
  transition: all 0.3s;

  > ul {
    position: relative;
    width: 100%;
    display: flex;
    flex-flow: column nowrap;
    height: 100%;
    max-height: calc(100vh - 140px);
    max-height: calc(100dvh - 140px);
    padding: 8px 0 40px 0 !important;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
    mask-image: linear-gradient(to bottom, black 95%, transparent 100%);

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: transparent;
      border-radius: 4px;
    }

    &:hover::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.2);
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }

  ${({ open }) =>
    open &&
    css`
      display: flex;
      position: fixed;
      z-index: 999;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      overflow-y: scroll;
      box-shadow: 2px 0 8px rgba(0, 0, 0, 0.4);
      border-right: 1px solid var(--menu-border-color);
    `}

  ${lg(css`
    position: sticky;
    top: 0;
    height: 100vh;
    height: 100dvh;
    flex-basis: 250px;
    display: flex;
    z-index: auto;
    right: auto;
    bottom: auto;
    left: auto;
    box-shadow: none;
    border-right: none;
  `)}
`;

export const MenuButton = styled.button`
  position: relative;
  width: 32px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 999;

  > span {
    position: absolute;
    left: 0;
    width: 100%;
    height: 3px;
    background: #fff;
    border-radius: 2px;
    transition:
      transform 0.4s ease,
      opacity 0.3s ease;
  }

  &,
  &:active,
  &:visited,
  &:focus,
  &:hover {
    display: flex;
    background-color: transparent;
    border: none;
    outline: 0;
    box-shadow: none;

    > svg {
      height: 20px;
      width: 20px !important;
    }

    ${lg(css`
      display: none;
    `)}
  }
`;

export const Header = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-end;
  border-bottom: 1px solid var(--menu-border-color);
  padding-right: var(--xs-padding-horizontal);
  height: var(--xs-header-height);

  ${xs(css`
    padding-right: var(--xs-padding-horizontal);
  `)}

  ${lg(css`
    padding: 26px var(--lg-padding-horizontal) !important;
    justify-content: center;
    height: var(--lg-header-height);
  `)}
`;

export const Brand = styled(Logo)`
  color: var(--menu-logo-color);
  fill: var(--menu-logo-color);
  flex: 0 0 auto;
  height: 20px;
`;

// Wraps the brand logo + label in a link to the dashboard, stacked and centered.
export const BrandLink = styled(Link)`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;

  /* No animated link underline (global a::before) on the logo. */
  &::before {
    display: none;
  }
`;

// "PORTAL" under the logo, matching the boot loader's label style.
export const BrandLabel = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3em;
  /* Compensate the trailing letter-spacing so the word stays centered. */
  padding-left: 0.3em;
  line-height: 1;
  color: var(--menu-logo-color);
`;

export const Footer = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-end;
  margin-top: 20px;
  padding: var(--xs-padding);
  padding-bottom: calc(var(--xs-padding-vertical) + env(safe-area-inset-bottom));

  ${lg(css`
    padding: 0 var(--lg-padding-horizontal) 26px var(--lg-padding-horizontal) !important;
  `)}
`;

export const FeedbackButton = styled(Button)`
  flex: 1;
  box-shadow: none;
  background-color: transparent;
  color: var(--text-white);
  border: 1px solid var(--menu-text);

  :hover,
  &:active {
    border: 1px solid var(--text-white);
    background-color: transparent;
    box-shadow: none;
  }

  :focus {
    outline: none;
    outline-offset: 1px;
  }

  svg {
    margin-right: var(--xs-padding-vertical);
  }

  ${lg(css`
    margin: 0;
  `)}
`;

export const MenuCategory = styled.li`
  color: var(--menu-category-color);
  font-size: var(--xs-font-size);
  text-transform: uppercase;
  font-weight: bold;
  padding: var(--xl-padding-vertical) var(--sm-padding-horizontal) var(--xs-padding-vertical) 13px;
  cursor: default;

  ${lg(css`
    padding-left: 25px;
  `)}
`;
